import type { FieldMetadata } from '../../../data/client';
import { useRecord } from '../../../data/hooks';
import { FieldDisplay } from '../../../fields/display/FieldDisplay';
import { FieldProvider } from '../../../fields/FieldContext';
import { Skeleton } from '../../../ui';
import { TransitionLink } from '../../nav/RouteTransition';
import { displayContext, recordTitle } from '../../records/table/tableModel';
import type { RelatedSection } from './sectionModel';
import { UnlinkAction } from './UnlinkAction';

/**
 * ONE related row.
 *
 * A CHILD row is the child record: its title links to its own page, its first few
 * values ride underneath.
 *
 * A JUNCTION row is a membership, and it deliberately does NOT link to itself (D7):
 * the junction's record page is a dead end - "Untitled", four fields, and no endpoint
 * on it, because the endpoints are system fields no screen may render. So the row
 * links to the PERSON and carries the relationship's own values - the role, the flag,
 * the dates - which is the entire justification for promoting a junction in the first
 * place. Section 7's "a related row opens its record" has exactly one exception and
 * this is it.
 */
export interface RelatedRowProps {
  readonly section: RelatedSection;
  readonly recordId: string;
  /**
   * The values shown under the title: the child's own for a child row, the
   * MEMBERSHIP's own (role, primary contact, joined at) for a junction row. One prop
   * for both, because it is one list and it has to be the SAME list the section put in
   * its `select` - two derivations would render a column the query never fetched.
   */
  readonly secondary: readonly FieldMetadata[];
  /** `junction` only: the endpoint field naming the other side. */
  readonly endpointFields: readonly FieldMetadata[];
  readonly readOnly: boolean;
}

export function RelatedRow({
  section,
  recordId,
  secondary,
  endpointFields,
  readOnly,
}: RelatedRowProps) {
  const record = useRecord(section.object.id, recordId);
  const row = record.data;

  if (row === undefined || row === null) {
    return (
      <li className="record-row record-row-stacked">
        <Skeleton className="h-4 w-40" />
      </li>
    );
  }

  /** The first endpoint is the one a removal is about; a self-join has two and either names it. */
  const endpoint = endpointFields[0];

  /**
   * WAVE 3 (R-B1): THE ROW'S VALUES ARE THE PRODUCT'S ONE VALUE LANGUAGE.
   *
   * `displayContext` on its own leaves a stored option as a tinted capsule, which is what the
   * owner saw as tag capsules inside the related sections (owner-37, owner-38) - the record's
   * own rows and the list's rows have drawn a dot beside plain text since round 2's finding F1,
   * and these were the last rows on the screen that had not been told.
   *
   * `listRow` - the other half of that decision (P5-17): inside a row a value is not an anchor,
   * because the row itself is the way to the record - is set on the SECONDARY values only. A
   * junction row's endpoint is the one exception the whole section rests on (D7): it IS the way
   * to the person, because the junction's own page is a dead end, so it stays a link.
   */
  const valueContext = (field: FieldMetadata, listRow: boolean) => ({
    ...displayContext(section.object.id, row, field),
    tagAsDot: true,
    listRow,
  });

  const body = (
    <div className="min-w-0 flex-1">
      {section.kind === 'junction' ? (
        /**
         * The endpoint rendered through the ordinary RELATION display, which links to
         * the target and - because the list query resolved the label in SQL - names it
         * without a request of its own (D3).
         */
        endpointFields.map((field) => (
          <div key={field.id} className="min-w-0 text-sm">
            <FieldProvider value={valueContext(field, false)}>
              <FieldDisplay />
            </FieldProvider>
          </div>
        ))
      ) : (
        <TransitionLink
          // A PUSH even though the depth is identical: opening a creator from an agency
          // goes one level further into the same task, and only the call site knows
          // that. It is the case a path-depth comparison could never get right, which
          // is why the direction is a prop.
          direction="push"
          to={`/objects/${section.object.id}/records/${recordId}`}
          /* NO UNDERLINE ON HOVER (wave 3 behaviour rule): a row is not a word in a
                 sentence, and macOS underlines nothing under the pointer. The accent role is
                 what says this is the way to the record. */
          className="block truncate text-sm font-medium text-accent"
        >
          {recordTitle(row, section.object.labelIdentifierFieldId)}
        </TransitionLink>
      )}

      {secondary.length > 0 && (
        <dl className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-2xs">
          {secondary.map((field) => (
            <div key={field.id} className="flex min-w-0 items-baseline gap-1.5">
              <dt className="sr-only">{field.label}</dt>
              <dd className="min-w-0 truncate text-ink-muted">
                <FieldProvider value={valueContext(field, true)}>
                  <FieldDisplay />
                </FieldProvider>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );

  /**
   * WAVE 4 (C13): the junction row's removal is the ROW's action, not a control drawn on it.
   * `UnlinkAction` wraps the row now - it is the context menu's trigger and the host of the
   * More that appears under the pointer - so a member row at rest is a name and its values,
   * exactly like every other row on this screen.
   */
  if (section.kind === 'junction' && !readOnly) {
    return (
      <li className="record-row record-row-stacked">
        <UnlinkAction
          junctionObjectId={section.object.id}
          junctionRecordId={recordId}
          endpointField={endpoint}
          endpointRecordId={
            endpoint === undefined || typeof row.values[endpoint.id] !== 'string'
              ? null
              : (row.values[endpoint.id] as string)
          }
          label={endpoint === undefined ? undefined : row.relationLabels?.[endpoint.id]}
        >
          {body}
        </UnlinkAction>
      </li>
    );
  }

  return <li className="record-row record-row-stacked">{body}</li>;
}
